import { ROLE_FIELD, TYPE_MODAL } from '../constants';
import {
  AddImageOutlineIcon,
  ArrowDown,
  EyeIcon,
  HideIcon,
  ImageIcon,
  TrashIcon,
  UnHideIcon,
} from '@/assets/svg';
import CarouselAnt from '@/components/CarouselAnt';
import Image from '@/components/Image';
import UploadCompAnt from '@/components/UploadCompAnt';
import ListCertificate from '@/components/UploadListImages';
import { formatListImage, handleSelectAll, mergeArrayByKey } from '@/helpers';
import { blobUrl } from '@/helpers';
import validateData from '@/helpers/validationHelpers/validationSchema';
import { CustomFormHelperText } from '@/module/all-screenings/component/ViewPatientRegistration/ModalInputServices';
import { WrapperButtonUpload } from '@/module/category/component/CategoryModal/styled';
import { UploadSingle } from '@/module/common/component';
import {
  ButtonSecondary,
  UploadAction,
} from '@/module/setup/createAndEditLocation.styled';
import { ClearComponent } from '@/new-components/ClearComponent';
import CustomCountryCodeInput from '@/new-components/CustomCountryCodeInput';
import CustomLoadingButton from '@/new-components/CustomLoadingButton';
import customToast from '@/new-components/CustomNotification';
import CustomSelectContainer from '@/new-components/CustomSelectOld/CustomSelectContainer';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  Paper,
  TextField,
  Typography,
  withStyles,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';

const FILE_SIZE = 2 * 1024 * 1024; // ~2mb
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'];

const titleStyles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
    color: theme.palette.grey.subText,
  },
  text: {
    fontWeight: 600,
  },
});

const CustomPaper = withStyles((theme) => ({
  root: {
    '& .MuiIconButton-root:hover': {
      backgroundColor: 'transparent',
    },

    '& .MuiAutocomplete-option': {
      padding: 8,
      borderRadius: 8,
      margin: '2px 8px',
      '&:first-child': {
        marginTop: 8,
      },
      '&:last-child': {
        marginBottom: 8,
      },
      '&.MuiAutocomplete-option[aria-selected="true"]': {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
}))(Paper);

const UploadText = withStyles({
  root: {
    color: '#333333',
  },
})(Typography);

const CustomAutocomplete = withStyles({
  root: {
    '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]':
      {
        paddingRight: 6,
        height: 36,

        '& .MuiAutocomplete-input:first-child': {
          display: (prop) => (prop.isSelected ? 'none' : ''),
        },
      },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input':
      {
        paddingRight: (prop) => (prop.isSelected ? 0 : 42),
      },
  },
})(Autocomplete);

const CustomDialogTitle = withStyles(titleStyles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <DialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" className={classes.text}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
});

const CustomDialog = withStyles({
  paper: {
    borderRadius: 16,
    width: 480,
  },
})(Dialog);

const CustomDialogContent = withStyles((theme) => ({
  root: {
    padding: 40,
    position: 'relative',
    '& .icon': {
      position: 'absolute',
      top: 10,
      right: 10,
    },

    '&:first-child': {
      paddingTop: 40,
    },
  },
}))(DialogContent);

const CustomDialogActions = withStyles({
  root: {
    margin: 0,
    padding: 16,
    justifyContent: 'flex-end',
  },
})(DialogActions);

const CustomInputLabel = withStyles((theme) => ({
  root: {
    color: '#666666',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}))(InputLabel);

const CustomTextField = withStyles({
  root: {
    fontSize: 20,
    width: '100%',

    '& .MuiOutlinedInput-root': {
      height: 48,
      borderRadius: 8,
    },
    '& .MuiOutlinedInput-input': {
      marginRight: 0,
      padding: '8px 5px',
      fontSize: 16,
    },
    '& .Mui-disabled': {
      '&.MuiOutlinedInput-root': {
        backgroundColor: '#f6f6f6',
      },
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      padding: 0,
    },
    '& .MuiFormHelperText-root': {
      alignItems: 'center',
      display: 'flex',
      fontFamily: 'OpenSans',
      fontSize: 12,
      gap: 6,
      marginLeft: 2,

      '& .MuiSvgIcon-root': {
        fontSize: 'unset',
      },
    },
  },
})(TextField);

const PreviewContent = withStyles({
  root: {
    width: 200,
    height: 200,

    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(51,51,51,0.4)',
    borderRadius: 8,

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
})(Box);

const CustomIconButton = withStyles({
  root: {
    minWidth: 'unset',
    width: 36,
    height: 36,
    '&.MuiButton-outlined': {
      padding: 0,
    },
  },
})(Button);

const useStyles = makeStyles({
  image: {
    width: 200,
    height: 200,
    borderRadius: 8,
  },
});

const UploadImageContainer = ({
  imageUrl,
  setFormValue,
  formValue,
  handleChangeForm,
}) => {
  const classes = useStyles();

  const [isHovered, setIsHovered] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const handlePreviewOpen = () => {
    setPreviewOpen(true);
    handleMouseLeave();
  };
  const handlePreviewClose = () => setPreviewOpen(false);

  const isPdf =
    imageUrl &&
    (typeof imageUrl === 'string'
      ? imageUrl.toLowerCase().endsWith('.pdf') || imageUrl.includes('.pdf')
      : imageUrl.type === 'application/pdf');
  const fileUrl = imageUrl
    ? typeof imageUrl === 'object'
      ? blobUrl(imageUrl)
      : imageUrl
    : null;

  return (
    <Box display="flex" gridGap={20}>
      <Box
        position="relative"
        onMouseEnter={() => handleMouseEnter('front')}
        onMouseLeave={handleMouseLeave}
      >
        {imageUrl ? (
          <>
            {isPdf ? (
              <Box
                width={200}
                height={200}
                bgcolor="#f6f6f6"
                border="1px solid #e1e1e1"
                borderRadius="8px"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <iframe
                  src={fileUrl}
                  width={200}
                  height={200}
                  style={{ border: 'none' }}
                  title="PDF Preview"
                />
              </Box>
            ) : (
              <Image
                src={
                  imageUrl
                    ? `${
                        typeof imageUrl === 'object'
                          ? blobUrl(imageUrl)
                          : imageUrl
                      }`
                    : null
                }
                alt="Preview"
                className={classes.image}
              />
            )}
            {isHovered && (
              <PreviewContent onClick={handlePreviewOpen}>
                <EyeIcon color="white" width={24} height={24} />
              </PreviewContent>
            )}
          </>
        ) : (
          <Box
            width={200}
            height={200}
            bgcolor="#f6f6f6"
            border="1px solid #e1e1e1"
            borderRadius="8px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <ImageIcon />
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        flexDirection="column"
        gridGap={16}
      >
        <Box>
          <UploadText>Upload Practising Certificate</UploadText>
          <UploadText>Recommended: PNG or JPEG or PDF</UploadText>
        </Box>
        <Box display="flex" gridGap={16}>
          {imageUrl && (
            <CustomIconButton
              color="primary"
              variant="outlined"
              onClick={() =>
                setFormValue({ ...formValue, praticisingCertificate: '' })
              }
            >
              <TrashIcon />
            </CustomIconButton>
          )}
          <UploadCompAnt
            isAddNew={true}
            label="Upload banner"
            fileData={imageUrl}
            onChange={handleChangeForm}
            editable={true}
            ActionComponent={
              <UploadAction>
                <ButtonSecondary>
                  {imageUrl ? 'Replace' : 'Upload'}
                </ButtonSecondary>
              </UploadAction>
            }
          />
        </Box>
      </Box>

      {previewOpen && (
        <Dialog
          disableBackdropClick
          open={previewOpen}
          onClose={handlePreviewClose}
          maxWidth="lg"
        >
          <CustomDialogContent>
            <IconButton className="icon" onClick={handlePreviewClose}>
              <CloseIcon />
            </IconButton>

            {isPdf ? (
              <iframe
                src={fileUrl}
                style={{ border: 'none', width: '800px', height: '80vh' }}
                title="PDF Preview"
              />
            ) : (
              <Image
                src={
                  imageUrl
                    ? `${
                        typeof imageUrl === 'object'
                          ? blobUrl(imageUrl)
                          : imageUrl
                      }`
                    : null
                }
                alt="Preview"
              />
            )}
          </CustomDialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export const arrayField = ({
  onChange,
  formValue,
  errors,
  listClinic,
  onDeleteCert,
  certShow,
  clinics,
  isClinicManager,
  setFormValue,
}) => {
  const renderValue = (array, label, defaultLabel) => {
    let value = '';
    if (array.length === 0) value = defaultLabel;
    else if (array.length === 1) value = array[0];
    else value = `${label} (${array.length})`;

    return value;
  };
  return [
    {
      key: 'mobile',
      render: (
        <>
          <CustomInputLabel style={{ marginTop: '20px' }}>
            Phone number
          </CustomInputLabel>
          <Box display="flex" gridGap={8}>
            <CustomCountryCodeInput
              country={formValue.countryCode}
              disabled={false}
              onChange={onChange('countryCode')}
            />
            <NumberFormat
              customInput={CustomTextField}
              variant="outlined"
              placeholder="Enter number"
              onValueChange={onChange('mobile')}
              allowNegative={false}
              value={formValue.mobile || ''}
            />
          </Box>
          {errors.mobile && (
            <CustomFormHelperText error>{errors.mobile}</CustomFormHelperText>
          )}
        </>
      ),
    },
    {
      key: 'imageUrl',
      render: (
        <>
          <CustomInputLabel style={{ marginTop: '20px' }}>
            Avatar
          </CustomInputLabel>
          <UploadSingle
            avatarCircle
            imageUrlSelected={formValue.imageUrl}
            onChange={onChange('imageUrl')}
            accept=".png,.jpg,.jpeg"
            uploadButton={
              <WrapperButtonUpload>
                <span className="avatar-icon">
                  <AddImageOutlineIcon color="#ccc" />
                </span>
                {'  '}
                <span className="text">Avatar</span>
              </WrapperButtonUpload>
            }
          />
        </>
      ),
    },
    {
      key: 'clinicId',
      render: (
        <>
          <CustomInputLabel style={{ marginTop: '20px' }}>
            Clinic
          </CustomInputLabel>
          <CustomAutocomplete
            getOptionSelected={(option, value) => option === value}
            getOptionLabel={(option) =>
              option.name && option.address
                ? `${option.name || ''}, ${option.address || ''}, ${
                    option.workingHours || ''
                  }`
                : ''
            }
            options={listClinic || []}
            value={
              listClinic.find((item) => item.id === formValue.clinicId) || ''
            }
            onChange={onChange('clinicId')}
            popupIcon={<ArrowDown />}
            renderInput={(params) => (
              <CustomTextField
                variant="outlined"
                placeholder="Select clinic"
                {...params}
              />
            )}
            disableClearable
            PaperComponent={({ children, ...other }) => (
              <CustomPaper {...other}>{children}</CustomPaper>
            )}
          />

          {errors.clinicId && (
            <CustomFormHelperText error>{errors.clinicId}</CustomFormHelperText>
          )}
        </>
      ),
    },
    {
      key: 'mcr',
      render: (
        <>
          <CustomInputLabel style={{ marginTop: '20px' }}>MCR</CustomInputLabel>
          <CustomTextField
            fullWidth
            variant="outlined"
            placeholder="Enter MRC"
            error={!!errors.mcr}
            helperText={errors.mcr}
            value={formValue.mcr || ''}
            onChange={onChange('mcr')}
          />
        </>
      ),
    },
    {
      key: 'praticisingCertificate',
      render: (
        <>
          <CustomInputLabel style={{ marginTop: '20px' }}>
            Practising Certificate
          </CustomInputLabel>
          <UploadImageContainer
            formValue={formValue}
            setFormValue={setFormValue}
            handleChangeForm={onChange('praticisingCertificate')}
            imageUrl={formValue?.praticisingCertificate}
          />
          {errors.praticisingCertificate && (
            <CustomFormHelperText error>
              {errors.praticisingCertificate}
            </CustomFormHelperText>
          )}
          {/* <UploadSingle
            typeUpload="normal"
            onChange={onChange('praticisingCertificate')}
            imageUrlSelected={formValue.praticisingCertificate}
            error={errors.praticisingCertificate}
            accept=".png,.jpg,.jpeg,.pdf"
            uploadButton={
              <WrapperButtonUpload>
                <span className="icon">
                  <AddImageOutlineIcon color="#ccc" />
                </span>{' '}
                <span className="text">Add Practising Certificate</span>
              </WrapperButtonUpload>
            }
          /> */}
        </>
      ),
    },
    {
      key: 'lstClinicId',
      render: (
        <>
          <CustomInputLabel style={{ marginTop: '20px' }}>
            Clinics
          </CustomInputLabel>

          <CustomSelectContainer
            isFullWidth
            height="48px"
            filterValue={clinics || []}
            data={listClinic.map((item) => ({
              key: item.id,
              value: `${item.name}, ${item.address}, ${item.unitNumber}`,
            }))}
            renderValue={renderValue(
              clinics.map(
                (it) => listClinic.find((item) => item.id === it).name
              ),
              'Clinics',
              'All clinics'
            )}
            clearable={false}
            onChange={onChange('lstClinicId')}
            labelSelectAll={'All clinics'}
            useSelectAll={isClinicManager ? false : true}
            checkedAll={listClinic.length === clinics.length}
            width={600}
          />

          {errors.lstClinicId && (
            <CustomFormHelperText error>
              {errors.lstClinicId}
            </CustomFormHelperText>
          )}
        </>
      ),
    },
    {
      key: 'lstCertificates',
      render: (
        <>
          <CustomInputLabel style={{ marginTop: '20px' }}>
            Certificate
          </CustomInputLabel>
          <Box pb={5}>
            <ListCertificate
              listImage={formValue.lstCertificates}
              onChange={onChange('lstCertificates')}
              onDelete={onDeleteCert}
              error={errors.lstCertificates}
            />
            <CarouselAnt data={certShow} disabled />
          </Box>
        </>
      ),
    },
  ];
};

const ModalAddOrEditUser = ({
  typeModal,
  open,
  onClose,
  onSubmit,
  selectedItem,
  listAdminRole,
  loading,
  listClinic,
  isClinicManager,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [certShow, setCertShow] = useState([]);
  const [clinics, setClinics] = useState([]);

  //list field to render when choose role
  const [renderField, setRenderField] = useState([]);
  const schema =
    typeModal === TYPE_MODAL.Edit ? 'adminEditSchema' : 'adminSchema';

  const [errors, setErrors] = useState({});

  const [formValue, setFormValue] = useState({
    password: '',
    confirmPassword: '',
    countryCode: 'SG',
    lstCertificates: [],
    lstClinicId: [],
  });

  //re render list field when role change
  useEffect(() => {
    if (formValue.roleType) {
      const mergeArr =
        mergeArrayByKey(
          ROLE_FIELD[get(formValue, 'roleType.roleType', formValue.roleType)],
          arrayField({
            onChange: handleChangeForm,
            formValue,
            errors,
            listClinic,
            certShow,
            onDeleteCert,
            clinics,
            isClinicManager,
            setFormValue,
          }),
          'key'
        ) || [];
      setRenderField(mergeArr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValue.roleType, certShow, errors, formValue]);
  //onChange format to show carousel list certificates
  useEffect(() => {
    if (
      !isEmpty(formValue.lstCertificates) &&
      !!formValue.lstCertificates.length
    ) {
      formatListImage(formValue.lstCertificates, setCertShow);
    }
  }, [formValue.lstCertificates]);

  useEffect(() => {
    if (typeModal === TYPE_MODAL.Edit) {
      let lstClinicId;
      if (!isEmpty(selectedItem.lstClinic)) {
        lstClinicId = selectedItem.lstClinic.map((clinic) => clinic.id);
        setClinics(lstClinicId);
      }
      const roleType = listAdminRole.find(
        (role) => role.roleType === selectedItem.roleType
      );
      setFormValue({
        ...formValue,
        ...selectedItem,
        lstClinicId,
        roleName: roleType.label,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeModal, selectedItem]);

  const onDeleteCert = (item, index) => {
    let lstCertificates = [...formValue.lstCertificates];
    lstCertificates.splice(index, 1);
    setFormValue({ ...formValue, lstCertificates });
  };

  const validateOnCreate = (errs) => {
    const hasNoPassword =
      typeModal === TYPE_MODAL.Create && !formValue.password;
    if (errs) {
      hasNoPassword
        ? setErrors({
            ...errs,
            password: 'Password is required',
          })
        : setErrors(errs);
    } else
      hasNoPassword
        ? setErrors({ password: 'Password is required' })
        : onSubmit(formValue);
  };

  const handleSubmit = async () => {
    try {
      await validateData(
        schema,
        {
          ...formValue,
          typeModal,
          roleType: get(formValue, 'roleType.roleType', formValue.roleType),
        },
        () => {
          validateOnCreate();
        }
      );
    } catch (errs) {
      validateOnCreate(errs);
    }
  };

  const handleChangeForm = (key) => (e, newValue) => {
    let value;
    switch (key) {
      case 'imageUrl':
      case 'praticisingCertificate':
        value = e;
        break;
      case 'roleType':
        value = newValue;
        break;
      case 'clinicId':
        value = newValue.id;
        break;
      case 'lstClinicId':
        value = handleSelectAll(
          e.target.value,
          listClinic.map((item) => item.id),
          clinics
        );
        setClinics(value);
        break;
      case 'lstCertificates':
        value = isEmpty(formValue.lstCertificates)
          ? []
          : [...formValue.lstCertificates];
        const file = e.file.originFileObj;
        if (!SUPPORTED_FORMATS.includes(get(file, 'type'))) {
          return customToast('error', 'Image is invalid');
        }
        if (get(file, 'size') > FILE_SIZE) {
          return customToast('error', 'Image is too large');
        }
        value.push(file);
        break;

      case 'mobile':
        value = e.value;
        break;

      default:
        value = get(e, 'target.value');
        break;
    }

    setFormValue({
      ...formValue,
      [key]: value,
      countryCode: isEmpty(formValue.countryCode)
        ? 'SG'
        : key === 'countryCode'
        ? value
        : formValue.countryCode,
    });
  };

  return (
    <CustomDialog
      scroll="body"
      disableEnforceFocus
      open={open}
      onClose={onClose}
      disableBackdropClick
    >
      <CustomDialogTitle onClose={onClose}>
        {typeModal === TYPE_MODAL.Create ? 'Create new user' : 'Edit user'}
      </CustomDialogTitle>
      <CustomDialogContent dividers>
        <CustomInputLabel>First name</CustomInputLabel>
        <CustomTextField
          fullWidth
          variant="outlined"
          placeholder="Enter name"
          error={!!errors.firstName}
          helperText={errors.firstName}
          value={formValue.firstName || ''}
          onChange={handleChangeForm('firstName')}
        />

        <CustomInputLabel style={{ marginTop: '20px' }}>
          Last name
        </CustomInputLabel>
        <CustomTextField
          fullWidth
          variant="outlined"
          placeholder="Enter name"
          error={!!errors.lastName}
          helperText={errors.lastName}
          value={formValue.lastName || ''}
          onChange={handleChangeForm('lastName')}
        />

        <CustomInputLabel style={{ marginTop: '20px' }}>Email</CustomInputLabel>
        <CustomTextField
          id="email"
          fullWidth
          variant="outlined"
          placeholder="name@email.com"
          autoComplete="off"
          inputProps={{ autoComplete: 'new-email' }}
          value={formValue.email || ''}
          onChange={handleChangeForm('email')}
          error={!!errors?.email}
          helperText={errors?.email}
          InputProps={{
            endAdornment: (
              <ClearComponent
                id="email"
                value={formValue.email}
                onClear={() => setFormValue({ ...formValue, email: '' })}
              />
            ),
          }}
        />

        {typeModal === TYPE_MODAL.Create && (
          <>
            <CustomInputLabel style={{ marginTop: 24 }}>
              Password
            </CustomInputLabel>
            <CustomTextField
              fullWidth
              variant="outlined"
              placeholder="12345678a"
              value={formValue.password || ''}
              onChange={handleChangeForm('password')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              inputProps={{ autoComplete: 'new-password' }}
              error={!!errors.password}
              helperText={
                errors.password ||
                'Password must be 8-32 characters long and includes at least one number, one letter (uppercase or lowercase), and may contain special characters like !@#$%^&*(),_-+='
              }
              InputProps={{
                endAdornment: (
                  <>
                    <ClearComponent
                      id="password"
                      value={formValue.password}
                      onClear={() =>
                        setFormValue({ ...formValue, password: '' })
                      }
                    />
                    <InputAdornment position="start">
                      <>
                        <HideIcon
                          style={{ cursor: 'pointer' }}
                          hidden={showPassword}
                          onClick={() => setShowPassword(true)}
                        />
                        <UnHideIcon
                          style={{ cursor: 'pointer' }}
                          hidden={!showPassword}
                          onClick={() => setShowPassword(false)}
                        />
                      </>
                    </InputAdornment>
                  </>
                ),
              }}
            />
            <CustomInputLabel style={{ marginTop: 24 }}>
              Confirm password
            </CustomInputLabel>
            <CustomTextField
              fullWidth
              variant="outlined"
              placeholder="12345678a"
              autoComplete="new-password"
              inputProps={{ autoComplete: 'new-password' }}
              value={formValue.confirmPassword || ''}
              onChange={handleChangeForm('confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <>
                    <ClearComponent
                      id="confirmPassword"
                      value={formValue.confirmPassword}
                      onClear={() =>
                        setFormValue({ ...formValue, confirmPassword: '' })
                      }
                    />

                    <InputAdornment position="start">
                      <>
                        <HideIcon
                          style={{ cursor: 'pointer' }}
                          hidden={showConfirmPassword}
                          onClick={() => setShowConfirmPassword(true)}
                        />
                        <UnHideIcon
                          style={{ cursor: 'pointer' }}
                          hidden={!showConfirmPassword}
                          onClick={() => setShowConfirmPassword(false)}
                        />
                      </>
                    </InputAdornment>
                  </>
                ),
              }}
            />
          </>
        )}
        <CustomInputLabel style={{ marginTop: '20px' }}>Role</CustomInputLabel>

        <CustomAutocomplete
          getOptionSelected={(option, value) =>
            option.roleType === value.roleType
          }
          getOptionLabel={(option) => option.label}
          options={listAdminRole || []}
          value={
            get(formValue, 'roleType.roleType')
              ? formValue.roleType
              : {
                  roleType: formValue.roleType,
                  label: formValue.roleName,
                }
          }
          onChange={handleChangeForm('roleType')}
          popupIcon={<ArrowDown />}
          renderInput={(params) => (
            <CustomTextField
              variant="outlined"
              placeholder="Select role"
              {...params}
            />
          )}
          disableClearable
          PaperComponent={({ children, ...other }) => (
            <CustomPaper {...other}>{children}</CustomPaper>
          )}
        />
        {errors.roleType && (
          <Typography color="error">{errors.roleType}</Typography>
        )}

        {renderField?.map((field) => (
          <Box key={field.key}>{field.render}</Box>
        ))}
      </CustomDialogContent>

      <CustomDialogActions>
        <CustomLoadingButton
          type="submit"
          variant="contained"
          color="primary"
          onClick={() => handleSubmit()}
          disabled={loading}
          loading={loading}
        >
          {typeModal === TYPE_MODAL.Create ? 'Create' : 'Save'}
        </CustomLoadingButton>
      </CustomDialogActions>
    </CustomDialog>
  );
};

ModalAddOrEditUser.propTypes = {
  typeModal: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  selectedItem: PropTypes.object,
};

ModalAddOrEditUser.defaultProps = {
  typeModal: TYPE_MODAL.Create,
  open: false,
  onClose: () => console.warn('Not implemented yet'),
  onSubmit: () => console.warn('Not implemented yet'),
  selectedItem: null,
};

export default ModalAddOrEditUser;
